/**
 * לקוח ה-API לניהול קטגוריות — TASKS 3.2.
 */
import type { AdminApiError } from './adminApi.js';

export interface Category {
  id: number;
  name: string;
  slug: string;
  sku_prefix: string;
  sort_order: number;
  image_url: string | null;
  is_active: number;
  created_at: string;
  updated_at: string;
}

async function parseErrorBody(res: Response): Promise<AdminApiError> {
  try {
    const body = (await res.json()) as { error?: { code?: string; message?: string } };
    return {
      status: res.status,
      code: body.error?.code ?? 'UNKNOWN_ERROR',
      message: body.error?.message ?? 'אירעה שגיאה לא צפויה.',
    };
  } catch {
    return { status: res.status, code: 'UNKNOWN_ERROR', message: 'אירעה שגיאה לא צפויה.' };
  }
}

export async function listCategories(): Promise<Category[]> {
  const res = await fetch('/api/admin/categories', { credentials: 'same-origin' });
  if (!res.ok) throw await parseErrorBody(res);
  const body = (await res.json()) as { categories: Category[] };
  return body.categories;
}

export async function suggestCategory(
  name: string,
): Promise<{ prefix: string; slug: string; prefixWasAdjusted: boolean }> {
  const res = await fetch(`/api/admin/categories/suggest?name=${encodeURIComponent(name)}`, {
    credentials: 'same-origin',
  });
  if (!res.ok) throw await parseErrorBody(res);
  return (await res.json()) as { prefix: string; slug: string; prefixWasAdjusted: boolean };
}

export async function createCategory(input: {
  name: string;
  slug: string;
  skuPrefix: string;
}): Promise<{ id: number }> {
  const res = await fetch('/api/admin/categories', {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  if (!res.ok) throw await parseErrorBody(res);
  return (await res.json()) as { id: number };
}

export async function updateCategory(
  id: number,
  input: { name?: string; slug?: string; isActive?: boolean },
): Promise<void> {
  const res = await fetch(`/api/admin/categories/${id}`, {
    method: 'PATCH',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  if (!res.ok) throw await parseErrorBody(res);
}

export async function reorderCategories(orderedIds: number[]): Promise<void> {
  const res = await fetch('/api/admin/categories/reorder', {
    method: 'PATCH',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ orderedIds }),
  });
  if (!res.ok) throw await parseErrorBody(res);
}

export async function deleteCategory(id: number): Promise<void> {
  const res = await fetch(`/api/admin/categories/${id}`, {
    method: 'DELETE',
    credentials: 'same-origin',
  });
  if (!res.ok) throw await parseErrorBody(res);
}

/** ⭐ סוגר את הפער מ-TASKS 6.3ב — קובץ בודד לכל קטגוריה, מוחלף בשלמותו. */
export async function uploadCategoryImage(id: number, file: File): Promise<{ imageUrl: string }> {
  const formData = new FormData();
  formData.append('file', file);
  const res = await fetch(`/api/admin/categories/${id}/image`, {
    method: 'PUT',
    credentials: 'same-origin',
    body: formData,
  });
  if (!res.ok) throw await parseErrorBody(res);
  return (await res.json()) as { imageUrl: string };
}
